import React from 'react';
import { BeakerIcon } from '@heroicons/react/24/outline';
import { VaccineService } from '../../../../shared/services/vaccineService';
import VaccineExpandRow from '../components/vaccineExpandRow';
import TabbedForm from '../../../../shared/components/forms/FormTabs';
import { StatusBadge } from '../../../../shared/components/ui';
import { SupplierService } from '../../../../shared/services/supplierService';
import apiService from '../../../../shared/services/apiService';

// 🔹 Dropdown loaders
const loadDropdownOptions = async (key) => {
  try {
    const res = await VaccineService.getAgeGroups(); // replace with generic dropdown API if needed
    const data = res?.data?.[key] || res?.data?.data?.[key] || res?.[key] || [];
    return data.map(item => ({ value: item.value, label: item.label }));
  } catch (error) {
    console.warn(`Failed to load dropdown options for ${key}`, error);
    return [];
  }
};

// Specific dropdowns
export const getAgeGroupOptions = () => loadDropdownOptions("vaccineAgeGroups");
export const getUserStatusOptions = () => loadDropdownOptions("userStatuses");
export const getVaccineTypeOptions = () => loadDropdownOptions("vaccineTypes");
export const getVialTypeOptions = () => loadDropdownOptions("vialTypes");
export const getDosageUnitsOptions = () => loadDropdownOptions("dosageUnits");

export const getSupplierOptions = async () => {
  try {
    const res = await SupplierService.getAll({
      status: "ACTIVE",
      limit: 5
    });

    // Extract suppliers array from nested response
    const data = res?.data?.data?.suppliers || res?.data?.suppliers || [];

    if (!Array.isArray(data)) {
      console.warn('Supplier data is not an array:', data);
      return [];
    }

    const options = data.map(supplier => ({
      value: supplier._id || supplier.id,
      label: supplier.supplierName || supplier.name || 'Unknown'
    }));

    return options;
  } catch (error) {
    console.error("Failed to load suppliers", error);
    return [];
  }
};

export const getVaccineFilterStatusOptions = async () => {
  try {
    const res = await apiService.getAll('dropdowns');
    const statuses =
      res?.data?.stockStatuses ||
      res?.stockStatuses ||
      [];

    // Return all statuses except 'ALL' (handled separately in filter UI)
    return statuses
      .filter(s => s.value !== 'ALL')
      .map(s => ({
        value: s.value,
        label: s.label
      }));
  } catch (error) {
    console.warn('Failed to load transfer filter statuses from dropdowns API', error);
    return [
      { value: 'DISPATCHED', label: 'Dispatched' },
      { value: 'IN_TRANSIT', label: 'In Transit' },
      { value: 'RECEIVED', label: 'Received' },
      { value: 'COMPLETED', label: 'Completed' },
      { value: 'CANCELLED', label: 'Cancelled' }
    ];
  }
};

export const vaccineConfig = {
  title: 'Vaccines',
  endpoint: 'admin/vaccines',
  crudEndpoint: 'vaccines',
  storageKey: 'vaccines',
  icon: BeakerIcon,
  useUrlNavigation: true,
  basePath: '/admin/vaccines',
  statusOptionsLoader: getVaccineFilterStatusOptions,
  columns: [
    { key: 'name', label: 'Vaccine Name', render: item => item.name || '—' },
   {
  key: 'ageGroupLabel',
  label: 'Age Group',
  render: item => {
    const labels = Array.isArray(item.ageGroupLabel)
      ? item.ageGroupLabel
      : item.ageGroupLabel
      ? item.ageGroupLabel.split(',').map(s => s.trim())
      : [];
    if (labels.length === 0) return <span className="text-gray-400">—</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {labels.map((label, i) => (
          <span
            key={i}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
          >
            {label}
          </span>
        ))}
      </div>
    );
  },
},
//     {
//   key: 'supplierId',
//   label: 'Manufacturer',
//   render: item => item?.supplierId?.supplierName || '—'
// },

    // { key: 'totalStock', label: 'Stock Quantity', render: item => item.totalStock ?? 0 },
    { key: 'status', label: 'Status', render: item => <StatusBadge status={item.status || 'ACTIVE'} /> }
  ],

 formFields: [
  // 🔹 Basic Info
  { name: 'name', label: 'Vaccine Name', type: 'text', required: true, placeholder: 'Enter vaccine name', section: 'basic' },
  { name: 'vaccineType', label: 'Vaccine Type', type: 'select', optionsLoader: getVaccineTypeOptions, required: true, placeholder: 'Select vaccine type', section: 'basic' },
  { name: 'vialType', label: 'Vial Type', type: 'select', optionsLoader: getVialTypeOptions, required: true, placeholder: 'Select vial type', section: 'basic' },
  { name: 'ageGroupLabel', label: 'Age Group', type: 'multiselect', optionsLoader: getAgeGroupOptions, required: true, placeholder: 'Select age groups', section: 'basic' },
  { 
    name: 'description', 
    label: 'Description', 
    type: 'richtext', 
    rows: 3, 
    placeholder: 'Enter description...', 
    fullWidth: true, 
    section: 'basic' 
  },
  { name: 'sideEffects', label: 'Side Effects', type: 'richtext', rows: 3, placeholder: 'Enter side effects...', fullWidth: true, section: 'basic' },
  { name: 'notes', label: 'Notes', type: 'richtext', rows: 3, placeholder: 'Enter notes...', fullWidth: true, section: 'basic' },

  // 🔹 Dosage
  { name: 'dosage', label: 'Dosage Instruction', type: 'text', required: true, placeholder: 'Enter dosage', section: 'dosage' },
  { name: 'dosageUnits', label: 'Dosage Units', type: 'select', optionsLoader: getDosageUnitsOptions, required: true, placeholder: 'Select dosage unit', section: 'dosage' },
  { name: 'dosagePerChild', label: 'Dosage per Child', type: 'number', placeholder: 'Enter dosage per child', section: 'dosage' },
  { name: 'dosagePerVial', label: 'Doses per Vial', type: 'number', placeholder: 'Enter doses per vial', section: 'dosage',tooltip: 'Number of doses per vial' },
  { name: 'childrenPerVial', label: 'Children per Vial', type: 'number', placeholder: 'Enter children per vial', section: 'dosage',tooltip: 'Number of children Vaccinated per vial' },
  { name: 'dosageCount', label: 'Number of Doses', type: 'number', placeholder: 'Enter dosage count', section: 'dosage'},
  { name: 'intervalBetweenDoses', label: 'Interval Between Doses', type: 'number', placeholder: 'Enter interval in days', section: 'dosage' },

  // 🔹 Manufacturing
//   {
//   name: 'supplierId', // 🔥 camelCase & backend-friendly
//   label: 'Manufacturer',
//   type: 'select',     // 🔥 dropdown, not text
//   required: true,
//   placeholder: 'Select manufacturer',
//   section: 'manufacturing',
//   optionsLoader: getSupplierOptions
// },

  // { name: 'supplierid', label: 'Manufacturer', type: 'text', required: true, placeholder: 'Enter manufacturer', section: 'manufacturing' },
  // { name: 'batchNumber', label: 'Batch Number', type: 'text', placeholder: 'Enter batch number', section: 'manufacturing' },
  // { name: 'manufacturingDate', label: 'Manufacturing Date', type: 'date', section: 'manufacturing' },
  // { name: 'expiryDate', label: 'Expiry Date', type: 'date', required: true, section: 'manufacturing' },

  // 🔹 Stock
  // { name: 'totalStock', label: 'Stock Quantity', type: 'number', placeholder: 'Enter stock quantity', section: 'stock' },
  // { name: 'price', label: 'Price (₹)', type: 'number', placeholder: 'Enter price', section: 'stock' },
  // // { name: 'minStockLevel', label: 'Minimum Stock Level', type: 'number', placeholder: 'Enter minimum stock', section: 'stock' },
  // { name: 'status', label: 'Status', type: 'select', optionsLoader: getUserStatusOptions, default: 'ACTIVE', section: 'stock' },

  // 🔹 Storage
  { name: 'storageLocation', label: 'Storage Location', type: 'text', placeholder: 'Enter storage location', section: 'storage' },
  { name: 'temperature', label: 'Temperature Range (°C)', type: 'text', placeholder: 'Enter temperature range', section: 'storage' },
  // { name: 'lastTemperatureCheck', label: 'Last Temperature Check', type: 'datetime-local', section: 'storage' },
  { name: 'storageCondition', label: 'Storage Conditions', type: 'textarea', rows: 3, placeholder: 'Enter storage conditions', fullWidth: true, section: 'storage' }
],


  searchFields: ['name', 'description', 'ageGroupLabel'],

  formState: { activeTab: 'basic' },

  customHandlers: {
    onView: async (item) => {
      const response = await VaccineService.getById(item.id);
      const data = response?.data || response;
      return data;
    },
    renderFormSections: (formFields, formData, handleFormChange, mode, renderFormField, activeTab, setActiveTab) => {
      const tabsConfig = {
        basic: { label: 'Basic Info' },
        dosage: { label: 'Dosage' },
        // manufacturing: { label: 'Manufacturing Details' },
        // stock: { label: 'Stock & Pricing' },
        storage: { label: 'Handling & Storage' }
      };
      return <TabbedForm formFields={formFields} formData={formData} handleFormChange={handleFormChange} renderFormField={renderFormField} tabsConfig={tabsConfig} activeTab={activeTab} setActiveTab={setActiveTab} />;
    },

    renderExpandedRow: (item) => <VaccineExpandRow item={item} />,
    processFormData: (formData, mode) => {
      const processed = {
        ...formData,
        totalStock: formData.totalStock ? parseInt(formData.totalStock, 10) : 0,
        price: formData.price ? parseFloat(formData.price) : 0,
        dosagePerChild: formData.dosagePerChild ? parseFloat(formData.dosagePerChild) : 0,
        dosagePerVial: formData.dosagePerVial ? parseInt(formData.dosagePerVial, 10) : 0,
        childrenPerVial: formData.childrenPerVial ? parseInt(formData.childrenPerVial, 10) : 0,
        dosageCount: formData.dosageCount ? parseInt(formData.dosageCount, 10) : 0,
        intervalBetweenDoses: formData.intervalBetweenDoses ? parseInt(formData.intervalBetweenDoses, 10) : 0,
        storageLocation: formData.storageLocation || null,
        temperature: formData.temperature || null,
        lastTemperatureCheck: formData.lastTemperatureCheck || null,
        storageCondition: formData.storageCondition || null
      };

      // Remove status if empty to use backend default
      if (!processed.status || processed.status === '') {
        delete processed.status;
      }

      // 🔹 exclude fields in edit mode
      if (mode === 'edit') {
        delete processed.totalStock;
        delete processed.price;
        delete processed.lastTemperatureCheck;
      }

      return processed;
    }


  }
};
